"use client"

import type React from "react"
import { useState } from "react"
import { useWallet } from "@solana/wallet-adapter-react"

interface WithdrawalFormProps {
  onWithdraw: (amount: number) => Promise<void>
  availableBalance: number
}

export function WithdrawalForm({ onWithdraw, availableBalance }: WithdrawalFormProps) {
  const { publicKey } = useWallet()
  const [loading, setLoading] = useState(false)
  const [amount, setAmount] = useState("")
  const [error, setError] = useState("")

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError("")

    if (!publicKey) { setError("Please connect your wallet"); return }

    const withdrawAmount = Number.parseFloat(amount)
    if (!withdrawAmount || withdrawAmount <= 0) { setError("Invalid amount"); return }
    if (withdrawAmount > availableBalance) { setError("Insufficient balance"); return }

    try {
      setLoading(true)
      await onWithdraw(withdrawAmount)
      setAmount("")
    } catch (err) {
      setError(err instanceof Error ? err.message : "Withdrawal failed")
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="w-full max-w-[480px] mx-auto">
      <div className="bg-[#13161C] rounded-3xl p-3 border border-white/5 shadow-2xl">
        <div className="px-1 mb-3">
          <div className="text-lg font-bold text-white mb-1">Withdraw Earnings</div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3">
          {/* Available Balance */}
          <div className="bg-[#1C1F26] rounded-2xl p-4 border border-[#25e4e8]/20">
            <label className="block text-sm text-[#8B949E] font-medium mb-2 ml-1">Available Balance</label>
            <div className="text-[32px] font-bold text-[#25e4e8]">{availableBalance.toFixed(2)} IRMA</div>
            {availableBalance === 0 && (
              <p className="text-sm text-[#8B949E] mt-2">
                You don't have any affiliate earnings yet. Earn credits when users subscribe through your referral link.
              </p>
            )}
          </div>

          {/* Amount Input */}
          <div className="bg-[#1C1F26] rounded-2xl p-4 pb-5 border border-transparent focus-within:border-white/10 transition">
            <label className="block text-sm text-[#8B949E] font-medium mb-3 ml-1">Withdrawal Amount (IRMA)</label>
            <input
              type="number"
              placeholder="0.00"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              min="0"
              step="0.01"
              disabled={loading}
              className="w-full bg-transparent text-right text-[32px] font-semibold outline-none text-white placeholder-[#3b4153] [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            />
          </div>

          {error && (
            <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-3 text-sm text-red-400">{error}</div>
          )}

          <div className="mt-3">
            <button
              type="submit"
              disabled={loading || !publicKey || availableBalance <= 0}
              className="w-full bg-[#25e4e8] hover:bg-[#1dd1d5] disabled:bg-[#8B949E] disabled:cursor-not-allowed text-black font-bold text-[17px] py-4 rounded-xl transition active:scale-[0.98] disabled:active:scale-100"
            >
              {loading ? "Processing..." : "Withdraw"}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
